"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ChefHat, Bath, House as Home, Building, Maximize2, Paintbrush, Sun, Hammer, ArrowRight, SlidersHorizontal as Sliders } from 'lucide-react';
interface ServiceCostData {
  id: string;
  title: string;
  description: string;
  link: string;
  icon: React.ElementType;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  factors: string[];
}
const servicesData: ServiceCostData[] = [{
  id: "kitchen",
  title: "Kitchen Remodeling",
  description: "Scope, cabinetry construction, and spatial reconfiguration determine structural and labor investment.",
  link: "/KitchenRemodelingPage",
  icon: ChefHat,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_cost_factors.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_cost_factors.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_cost_factors.png-wvc-sizes",
    alt: "Custom Brookhaven kitchen with inset cabinetry and central island"
  },
  factors: ["Square footage", "Cabinet and material grade", "Appliance tier", "Structural changes", "Permitting scope"]
}, {
  id: "bathroom",
  title: "Bathroom Remodeling",
  description: "Waterproofing integrity, precision tilework, and fixture selection establish the primary baseline.",
  link: "/BathroomRemodelingPage",
  icon: Bath,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_cost_factors.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_cost_factors.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_bathroom_cost_factors.webp-wvc-sizes",
    alt: "Walk-in shower renovation with frameless glass and natural stone tile"
  },
  factors: ["Fixture tier", "Waterproofing scope", "Layout complexity", "Square footage", "Plumbing relocation"]
}, {
  id: "whole-home",
  title: "Whole-Home Remodeling",
  description: "Multifaceted mechanical updates, wall alterations, and holistic finishes across connected living spaces.",
  link: "/WholeHomeInteriorRemodelingPage",
  icon: Home,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_factors.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_factors.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_factors.png-wvc-sizes",
    alt: "Open floor plan whole-home renovation with hardwood floors and architectural trim"
  },
  factors: ["Number of rooms", "Structural work", "Systems upgrades", "Square footage", "Permitting complexity"]
}, {
  id: "home-additions",
  title: "Home Additions",
  description: "Expanding the structural footprint requires foundation stability, architectural tying, and structural framing.",
  link: "/HomeAdditionsPage",
  icon: Building,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_factors.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_factors.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_factors.png-wvc-sizes",
    alt: "Two-story residential home addition tie-in during exterior finish phase"
  },
  factors: ["Foundation work", "Roofline tie-in", "Square footage added", "Structural complexity", "Permitting scope"]
}, {
  id: "basement",
  title: "Basement Remodeling",
  description: "Sub-grade moisture control, utility access requirements, and safety egress establish the core scope.",
  link: "/BasementRemodelingPage",
  icon: Maximize2,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_factors.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_factors.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_factors.png-wvc-sizes",
    alt: "Finished basement living room with recessed lighting and egress window"
  },
  factors: ["Waterproofing scope", "Ceiling height and structural work", "Egress windows", "Finish level", "Square footage"]
}, {
  id: "interior",
  title: "Interior Remodeling",
  description: "Reallocating rooms, modernizing electrical layouts, and millwork upgrades across specific living zones.",
  link: "/InteriorRemodelingPage",
  icon: Paintbrush,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_factors.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_factors.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_factors.png-wvc-sizes",
    alt: "Renovated living room interior featuring custom millwork and updated lighting"
  },
  factors: ["Square footage affected", "Material grade", "Structural wall removal", "Electrical and lighting scope"]
}, {
  id: "outdoor-living",
  title: "Outdoor Living",
  description: "Protected covered structures, exterior weatherproofing, and utility extensions shape outdoor spaces.",
  link: "/OutdoorLivingPage",
  icon: Sun,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_factors.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_factors.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_factors.png-wvc-sizes",
    alt: "Covered outdoor patio with cedar beams, stone pavers, and exterior lighting"
  },
  factors: ["Square footage", "Structural covering type", "Utility runs", "Material grade"]
}, {
  id: "decks",
  title: "Deck Remodeling",
  description: "Framing stability, terrain slope, premium composite or hardwood boards, and guardrail requirements.",
  link: "/DeckRemodelingPage",
  icon: Hammer,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_deck_factors.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_deck_factors.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_deck_factors.png-wvc-sizes",
    alt: "Elevated residential deck renovation with composite decking and cable railings"
  },
  factors: ["Square footage", "Material tier", "Structural condition", "Railing and stair complexity"]
}];
export default function ServiceCostFactors() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="servicecostfactors" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 border-b border-border transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 text-primary font-medium text-sm tracking-wide uppercase mb-3">
            <Sliders className="size-4" />
            <span>Investment Planning</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground text-balance">
            Key cost-driving factors for every remodeling service
          </h2>
          <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed font-default">
            Every renovation carries unique structural, material, and site requirements. Understanding the primary drivers behind each scope helps plan accurate priorities before construction begins.
          </p>
        </div>

        {/* Services Cost Factor Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 lg:gap-10">
          {servicesData.map((service, index) => {
          const Icon = service.icon;
          return <Card key={service.id} data-index={index} className="bg-card text-card-foreground border border-border shadow-xs hover:shadow-md transition-shadow duration-200 overflow-hidden flex flex-col justify-between">
                <div>
                  {/* Photo Visual */}
                  <div className="relative aspect-16/9 w-full overflow-hidden bg-muted">
                    <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                    <div className="absolute top-4 left-4">
                      <Badge variant="secondary" className="bg-secondary/90 text-secondary-foreground backdrop-blur-xs flex items-center gap-1.5 px-3 py-1 text-xs font-normal">
                        <Icon className="size-3.5 text-primary" />
                        <span>Scope Breakdown</span>
                      </Badge>
                    </div>
                  </div>

                  {/* Card Header & Content */}
                  <CardHeader className="pt-6 pb-2">
                    <CardTitle className="font-serif text-2xl font-medium tracking-tight text-card-foreground">
                      {service.title}
                    </CardTitle>
                    <p className="text-sm text-foreground/80 mt-1 leading-normal font-default">
                      {service.description}
                    </p>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6">
                    <Separator className="my-3 bg-border/60" />
                    <div className="space-y-2 mt-4">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider">
                        Primary cost variables:
                      </p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        {service.factors.map((factor, factorIdx) => <li key={factorIdx} data-index={factorIdx} className="flex items-center gap-2 text-sm text-foreground/90 bg-muted/50 rounded-md px-2.5 py-1.5 border border-border/50">
                            <span className="size-1.5 rounded-full bg-primary shrink-0" />
                            <span className="truncate">{factor}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </div>

                {/* Footer Link */}
                <div className="px-6 pb-6 pt-0 border-t border-border/30 mt-auto">
                  <div className="pt-4 flex items-center justify-between">
                    <span className="text-xs text-foreground/70">
                      Explore detailed scope
                    </span>
                    <Button asChild variant="ghost" size="sm" className="gap-1.5 text-foreground hover:text-primary transition-colors -mr-2">
                      <Link to={service.link}>
                        <span>View service</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>;
        })}
        </div>

        {/* Consultation Call to Action Strip */}
        <div className="mt-16 bg-muted/60 border border-border rounded-xl p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="font-serif text-xl sm:text-2xl font-medium text-foreground">
              Request an in-home assessment for your project
            </h3>
            <p className="mt-2 text-sm sm:text-base text-foreground/80">
              We review site conditions, load-bearing requirements, and architectural plans to assemble a dependable project scope.
            </p>
          </div>
          <div className="shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestAnEstimatePage">
                Schedule an estimate
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}